/**
 * PHASE 2: PortfolioContext migré vers React Query
 * Version hybride qui utilise React Query en interne mais expose la même interface
 * pour une migration progressive sans casser le code existant
 */
import React, { createContext, useContext, useMemo, useCallback } from 'react';
import { useIBKRStatus, usePositions, useAccount, useConnectIBKR, useDisconnectIBKR } from '../hooks/useIBKR';
import { useQueryClient } from '@tanstack/react-query';

const PortfolioContext = createContext(null);

export const usePortfolio = () => {
  const context = useContext(PortfolioContext);
  if (!context) {
    throw new Error('usePortfolio must be used within a PortfolioProvider');
  }
  return context;
};

export const PortfolioProvider = ({ children }) => {
  const queryClient = useQueryClient();
  const autoConnectAttemptedRef = React.useRef(false);

  // Utiliser les hooks React Query pour les données
  const { data: statusData, isLoading: statusLoading, error: statusError } = useIBKRStatus();
  const isConnected = statusData?.is_connected ?? false;

  const { mutate: connectMutation, isPending: isConnecting } = useConnectIBKR();
  const { mutate: disconnectMutation, isPending: isDisconnecting } = useDisconnectIBKR();

  // Connexion IBKR en tâche de fond au chargement de l'app (même sans utilisateur connecté)
  // En production (origine myportfolio.beam-consulting.fr), ne pas auto-appeler connect :
  // le backend est sur le VPS et 127.0.0.1 = VPS, pas le Gateway sur une autre machine → 500 évité.
  const isProductionOrigin = typeof window !== 'undefined' && window.location.hostname === 'myportfolio.beam-consulting.fr';
  React.useEffect(() => {
    if (isProductionOrigin || statusLoading || isConnected || autoConnectAttemptedRef.current) return;
    autoConnectAttemptedRef.current = true;
    connectMutation(undefined, {
      onSettled: () => {
        queryClient.invalidateQueries({ queryKey: ['tws', 'status'] });
      },
    });
  }, [isProductionOrigin, statusLoading, isConnected, connectMutation, queryClient]);

  // Passer isConnected aux hooks pour éviter les appels inutiles quand TWS n'est pas connecté
  const { data: positionsData, isLoading: positionsLoading, error: positionsError } = usePositions({ isConnected });
  const { data: accountData, isLoading: accountLoading, error: accountError } = useAccount({ isConnected });
  
  // Extraire les valeurs (isConnected déjà défini ci-dessus)
  const positions = positionsData || [];
  const account = accountData || null;
  const loading = statusLoading || positionsLoading || accountLoading || isConnecting || isDisconnecting;
  const error = statusError || positionsError || accountError;
  
  // Fonctions de compatibilité avec l'ancien contexte
  const checkStatus = useCallback(async () => {
    await Promise.resolve(queryClient.refetchQueries({ queryKey: ['tws', 'status'] }));
    const data = queryClient.getQueryData(['tws', 'status']);
    return data?.is_connected ?? false;
  }, [queryClient]);
  
  const loadData = useCallback(async (force = false, silent = false) => {
    if (force) {
      // Invalider suffit : React Query refetch automatiquement les queries actives
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['tws', 'positions'] }),
        queryClient.invalidateQueries({ queryKey: ['tws', 'account'] }),
        queryClient.invalidateQueries({ queryKey: ['tws', 'status'] }),
      ]);
    }
  }, [queryClient]);
  
  const refreshData = useCallback(() => loadData(true), [loadData]);
  
  const loadAccount = useCallback(async () => {
    await Promise.resolve(queryClient.refetchQueries({ queryKey: ['tws', 'account'] }));
    return queryClient.getQueryData(['tws', 'account']);
  }, [queryClient]);
  
  const loadPositions = useCallback(async () => {
    await Promise.resolve(queryClient.refetchQueries({ queryKey: ['tws', 'positions'] }));
    return queryClient.getQueryData(['tws', 'positions']) || [];
  }, [queryClient]);
  
  const connect = async () => {
    return new Promise((resolve, reject) => {
      connectMutation(undefined, {
        onSuccess: () => {
          const refetchPromise = queryClient.refetchQueries({ queryKey: ['tws', 'status'] });
          Promise.resolve(refetchPromise).then(() => {
            const statusData = queryClient.getQueryData(['tws', 'status']);
            const isActuallyConnected = statusData?.is_connected ?? false;
            resolve(isActuallyConnected);
          }).catch((err) => {
            // Même en cas d'erreur de vérification, considérer la connexion comme réussie si la mutation a réussi
            resolve(true);
          });
        },
        onError: (error) => {
          if (process.env.NODE_ENV === 'development') {
            console.error('❌ [PortfolioContextV2] Erreur de connexion:', error);
          }
          reject(error);
        },
      });
    });
  };
  
  const disconnect = async () => {
    return new Promise((resolve, reject) => {
      disconnectMutation(undefined, {
        onSuccess: () => {
          resolve(true);
        },
        onError: (error) => {
          reject(error);
        },
      });
    });
  };
  
  // Calculer lastUpdate depuis les timestamps des queries
  const lastUpdate = useMemo(() => {
    const positionsState = queryClient.getQueryState(['tws', 'positions']);
    const accountState = queryClient.getQueryState(['tws', 'account']);
    
    if (positionsState?.dataUpdatedAt && accountState?.dataUpdatedAt) {
      return Math.max(positionsState.dataUpdatedAt, accountState.dataUpdatedAt);
    }
    return positionsState?.dataUpdatedAt || accountState?.dataUpdatedAt || null;
  }, [queryClient, positionsData, accountData]);
  
  // Background loading (si une query est en cours mais pas encore visible)
  const backgroundLoading = useMemo(() => {
    const positionsState = queryClient.getQueryState(['tws', 'positions']);
    const accountState = queryClient.getQueryState(['tws', 'account']);
    return (positionsState?.isFetching || accountState?.isFetching) && !loading;
  }, [queryClient, loading]);
  
  // Valeur du contexte (même interface que l'ancien)
  const contextValue = useMemo(() => ({
    isConnected,
    account,
    positions,
    loading,
    backgroundLoading,
    error: error?.message || null,
    lastUpdate,
    checkStatus,
    loadData,
    refreshData,
    loadAccount,
    loadPositions,
    connect,
    disconnect,
  }), [
    isConnected,
    account,
    positions,
    loading,
    backgroundLoading,
    error,
    lastUpdate,
    checkStatus,
    loadData,
    refreshData,
    loadAccount,
    loadPositions,
    connect,
    disconnect,
  ]);

  return (
    <PortfolioContext.Provider value={contextValue}>
      {children}
    </PortfolioContext.Provider>
  );
};

